<template>
   <div v-if="!this.$store.state.isPortrait" class="articles-container site-section" :class="{entering: entering, hidden: !articlesEntered}" ref="articlesContainer">
      <Stage ref="articlesStage" :class="{hidden: !articlesEntered}">
         <transition :name="this.$store.state.prevArticleIdx < this.$store.state.activeArticleIdx ? 'article-stage-down' : 'article-stage-up'">
            <ArticleStageContent
                    v-for="(article, index) in articles"
                    v-if="$store.state.activeArticleIdx === index"
                    :article="article"
                    :key="article.number"
                    :index="index"
                    ref="articlesStage"
            >
            </ArticleStageContent>
         </transition>
      </Stage>
      <ScrollContent ref="articlesScrollContent">
         <Article
                 v-for="(article, index) in articles"
                 :article="article"
                 :key="article.number"
                 :index="index"
                 :id="'section-' + article.number"
         >
         </Article>
      </ScrollContent>
      <div class="articles-end" ref="articlesEnd"></div>
   </div>
   <div class="articles-container site-section mobile" v-else>
      <div v-for="(article, index) in articles" class="mobile-article" :id="'section-' + article.number" :key="article.number">
         <ArticleStageContent :article="article"></ArticleStageContent>
         <Article :article="article"></Article>
      </div>
      <div class="articles-end" ref="articlesEnd"></div>
   </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import 'TweenMax'
    import ScrollMagic from 'scrollmagic'
    import 'debug.addIndicators'

    import content from './../content'
    import {bus, smController, html} from '../consts'

    import Stage from './Stage'
    import ArticleStageContent from './ArticleStageContent'
    import ScrollContent from './ScrollContent'
    import Article from './Article'

    export default {
        name: 'Articles',
        components: {
            Stage,
            ArticleStageContent,
            ScrollContent,
            Article
        },
        data() {
            return {
                articles: content.articles,
                sideNavLinks: null,
                entering: true,
                smController: new ScrollMagic.Controller(),
                articlesIntroScene: new ScrollMagic.Scene(),
                articlesEndScene: new ScrollMagic.Scene()
            }
        },
        computed: {
            ...mapGetters({
                'articlesEntered': 'getArticlesEnteredState'
            })
        },
        methods: {
            handleEnterAnim() {
                this.entering = true;
                setTimeout(() => {
                    // trigger the enter animations for the article intro-text elements
                    this.entering = false;
                }, 400);
            },
            handleArticlesEntered(scrollDestination) {
                // upon activation of the Articles component, scroll the window down to the position of the desired article
                if (this.$store.state.activeArticleIdx > 0) {
                    let activeArticle = document.getElementById('section-' + (this.$store.state.activeArticleIdx + 1));
                    let activeArticleRect = activeArticle.getBoundingClientRect();
                    if (scrollDestination === 'bottom') {
                        // if we came from the Conclusions section, scroll to the bottom of the articles section
                        TweenMax.set(window, {scrollTo: {y: 999999, autoKill: false}});
                    } else {
                        // otherwise scroll to the top of the article
                        TweenMax.set(window, {scrollTo: {y: activeArticleRect.top, autoKill: false}});
                    }
                }
            }
        },
        mounted() {
            bus.$on('articlesEntered', this.handleArticlesEntered);
            bus.$on('articlesEnterAnim', this.handleEnterAnim);

            if (!this.$store.state.isPortrait) {
                this.articlesIntroScene
                    .triggerElement(this.$refs.articlesScrollContent.$el)
                    .on('start', (e) => {
                        if (e.scrollDirection === 'FORWARD') {
                            if (!this.$store.state.articlesEntered) {
                                this.entering = false;
                                if (this.$store.state.magicAllowed) {

                                    // reset the active article index so that scrolling down will always take us to the first article
                                    // only necessary when someone has jumped back to the top using the sideNavigation buttons
                                    if (this.$store.state.clickedIntroNavBtn) {
                                        this.$store.commit('setActiveArticleIdx', 0);
                                        this.$store.commit('setPrevArticleIdx', -1);
                                        this.$store.commit('clickedIntroNavBtn', false);
                                    }
                                }
                                this.$store.commit('setArticlesEntered', true);
                            }
                            this.$store.commit('setView', 'articles');
                        } else if (e.scrollDirection === 'REVERSE') {
                            this.entering = true;
                            this.$store.commit('setArticlesEntered', false);
                            this.$store.commit('setView', 'intro');
                        }
                    })
                    .addTo(this.smController)
                    //.addIndicators({name: 'articlesIntro'});

            }

            this.articlesEndScene
                .triggerHook('onEnter')
                .triggerElement(this.$refs.articlesEnd)
                .offset(-25)
                .on('start', (e) => {
                    if (e.scrollDirection === 'FORWARD') {
                        this.$store.commit('setView', 'conclusions');
                    } else if(e.scrollDirection === 'REVERSE') {
                        this.$store.commit('setView', 'articles');
                    }
                })
                .addTo(this.smController)
                //.addIndicators({name: 'articlesEnd'});
        },
        beforeDestroy() {
            bus.$off('articlesEntered', this.handleArticlesEntered);
            bus.$off('articlesEnterAnim', this.handleEnterAnim);
        }
    }
</script>

<style lang="scss">
   @import "../scss/_the-goods.scss";

   .articles-container {
      position         : relative;
      width            : 100%;
      z-index          : 2;
      background-color : $color-light-grey;

      &.mobile {

         .stage-content {
            position : relative;
            height   : 130vw;
            overflow : hidden;

            @include screen-md {
               height : 75vh;
            }
         }
      }

      &.entering {

         .article {

            .enter-anim {
               transition : none;
               transform  : translateY(40px) !important;
               opacity    : 0 !important;
            }
         }
      }

      &.entering.hidden {

         .article {

            .enter-anim {
               transition : opacity 0.5s $default-bez, transform 0.5s $default-bez;
               transition-delay: 0s !important;
               transform  : translateY(40px) !important;
               opacity    : 0 !important;
            }
         }
      }
   }

   .att-stage {
      transition : transform .4s $default-bez;

      &.hidden {
         transform : translateY(-100%);
      }

      img {
         object-fit : cover;
      }
   }

   // ArticleStageContent component enter transition classes
   .article-stage-down-enter-active,
   .article-stage-down-leave-active,
   .article-stage-up-enter-active,
   .article-stage-up-leave-active {
      transition : transform .4s $default-bez;
   }

   .article-stage-down-enter,
   .article-stage-up-leave-to {
      transform : translateY(-100%);
   }

   .article-stage-down-leave-to,
   .article-stage-up-enter {
      transform : translateY(100%);
   }

   .article-stage-down-enter-to,
   .article-stage-down-leave,
   .article-stage-up-enter-to,
   .article-stage-up-leave {
      transform : translateY(0%);
   }

</style>



// WEBPACK FOOTER //
// src/components/Articles.vue